import React, { useCallback, useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import {
    CONSTANTS,
    ERROR_CODE,
    WMICLogger,
    LINE_OF_BUSINESS,
    JobType,
    WMICVariousUtil,
    parseErrorMessage,
} from 'wmic-pe-portals-utils-js';
import { TranslatorContext } from '@jutro/locale';
import { WMICWizardChangeOrRenewalPage, useDocumentTitle, useWizardModals } from 'wmic-pe-portals-wizard-components-ui';
import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { withViewModelService, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { WMICUnderwritingIssues } from 'wmic-pe-components-platform-react';
import { WMICHOPrintableAgreement } from 'wmic-pe-capability-gateway-common-ho-react';
import { WMICDocuments, WMICQuoteSummary } from 'wmic-pe-capability-gateway-common-react';
import { WMICErrorHandler, WMICQuoteUtil } from 'wmic-pe-capability-quoteandbind-common-react';
import WMICUIIssuesModal from 'wmic-pe-capability-quoteandbind-common-react/pages/WMICPaymentPage/WMICUIIssuesModal/WMICUIIssuesModal';

import { messages as commonMessages } from 'wmic-pe-capability-gateway-policyrenewal-common-react';
import metadata from './WMICPolicyRenewalQuote.metadata.json5';
import messages from './WMICPolicyRenewalQuote.messages';

function WMICPolicyRenewalQuote(props) {
    const { wizardData: renewalVM, updateWizardData, markStepSubmitted, stopErrorNavigation } = props;
    const history = useHistory();
    const { RenewalService } = useDependencies('RenewalService');
    const { showConfirm, showCustom, showError } = useWizardModals();
    const { authHeader } = useAuthentication();
    const { isComponentValid, onValidate, initialValidation, registerComponentValidation } =
        useValidation('WMICPolicyRenewalQuote');
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const [userDocuments, setUserDocuments] = useState([]);
    const { setWizardLoading } = useWizardModals();
    const [renewalSummaryVM, setRenewalSummaryVM] = useState({});
    const jobID = _.get(renewalVM.value, 'jobID');

    useDocumentTitle(translator(messages.renewalQuoteTitle), renewalVM);

    const underwritingIssues = _.get(renewalVM, 'errorsAndWarnings.underwritingIssues.value', []);

    const hasQuoteBlockingUWIssues = useCallback(() => WMICQuoteUtil.hasQuoteBlockingUWIssues(renewalVM), [renewalVM]);

    const isTransactionQuoted = useCallback(() => renewalVM?.statusCode?.value?.code === CONSTANTS.QUOTED, [renewalVM]);

    const checkComponentValidation = useCallback(
        () =>
            !(
                WMICQuoteUtil.isBlockingUWIssuePresent(renewalVM) ||
                WMICQuoteUtil.hasValidationErrors(renewalVM) ||
                hasQuoteBlockingUWIssues() ||
                !isTransactionQuoted()
            ),
        [renewalVM, hasQuoteBlockingUWIssues, isTransactionQuoted]
    );

    useEffect(() => {
        registerComponentValidation(checkComponentValidation);
        stopErrorNavigation();
    }, [registerComponentValidation, checkComponentValidation, stopErrorNavigation]);

    const fetchRenewalSummary = useCallback(async () => {
        const LOB = _.get(renewalVM, 'value.lob');
        let vmType = '';
        let renewalResult = {};

        if (LOB === LINE_OF_BUSINESS.PERSONAL_AUTO) {
            vmType = 'wmic.edge.ca.capabilities.gateway.job.renewal.dto.PARenewalReviewDTO_WMIC';
            renewalResult = await RenewalService.getPARenewalSummary_WMIC(jobID, authHeader);
        } else if (LOB === LINE_OF_BUSINESS.HOME_OWNER) {
            vmType = 'wmic.edge.ca.capabilities.gateway.job.renewal.dto.HORenewalReviewDTO_WMIC';
            renewalResult = await RenewalService.getHORenewalSummary_WMIC(jobID, authHeader);
        } else if (LOB === LINE_OF_BUSINESS.PERSONAL_UMBRELLA) {
            vmType = 'wmic.edge.ca.capabilities.gateway.job.renewal.dto.PUPRenewalReviewDTO_WMIC';
            renewalResult = await RenewalService.getPUPRenewalSummary_WMIC(jobID, authHeader);
        }

        const renewalSummary = viewModelService.create(renewalResult, 'pc', vmType, renewalVM.aspects.context());
        setRenewalSummaryVM(renewalSummary);
        setUserDocuments(renewalSummary.documents.value);
        renewalVM.errorsAndWarnings.value = renewalSummary.errorsAndWarnings.value;
        updateWizardData(renewalVM);
        markStepSubmitted();
    }, [renewalVM, viewModelService, RenewalService, jobID, authHeader]);

    const loadRenewalSummary = useCallback(async () => {
        setWizardLoading(true, translator(commonMessages.loading));
        await fetchRenewalSummary()
            .catch(error => {
                WMICLogger.error('Unable to load renewal summary', error);
                WMICErrorHandler.processAsModal(parseErrorMessage(error)).catch(() =>
                    history.push(`/renewal/${jobID}/summary`)
                );
            })
            .finally(() => setWizardLoading(false));
    }, [
        setWizardLoading,
        translator,
        fetchRenewalSummary,
        history,
        jobID,
    ]);

    useEffect(() => {
        loadRenewalSummary();
    }, [loadRenewalSummary]);

    const showUWIssueModal = (uwIssues, jobId) => {
        showCustom(<WMICUIIssuesModal uiIssues={uwIssues} />)
            .then(() => {
                history.push(`/renewal/${jobId}/summary`);
            })
            .catch(() => {
                _.noop();
            });
    };

    const onNext = useCallback(() => {
        try {
            if (!isComponentValid) {
                WMICVariousUtil.scrollToTop();
                return false;
            }

            return showConfirm({
                title: messages.bindpolicytitle,
                message: messages.bindmessage,
                confirmButtonText: messages.bindpolicy,
                cancelButtonText: messages.cancelbutton,
            })
                .then(async results => {
                    if (results === CONSTANTS.MODAL_RESULT.CANCEL || results === CONSTANTS.MODAL_RESULT.CLOSE) {
                        updateWizardData(renewalVM);

                        return false;
                    }

                    setWizardLoading(true, translator(messages.binding));

                    const bindResponse = await RenewalService.bind_WMIC(jobID, authHeader)
                        .then(response => {
                            _.extend(renewalVM.value, response);
                            updateWizardData(renewalVM);
                            history.push(`/policies/${response.policyNumber}/${renewalVM.value.termNumber}/summary`);
                            return renewalVM;
                        })
                        .catch(error => {
                            if (error.errorCode === ERROR_CODE.GW_UNDERWRITING_ERROR) {
                                const uwIssues = WMICErrorHandler.extractUWIssuesFromErrorMessage(error);
                                showUWIssueModal(uwIssues, jobID);
                            } else {
                                showError({
                                    title: translator(messages.error),
                                    message: translator(messages.bindingFailure),
                                });
                            }
                            return false;
                        });

                    return bindResponse;
                }, _.noop)
                .finally(() => {
                    setWizardLoading(false);
                });
        } finally {
            setWizardLoading(false);
        }
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isComponentValid,
        showConfirm,
        setWizardLoading,
        translator,
        RenewalService,
        jobID,
        authHeader,
        renewalVM,
        history,
    ]);

    // This function is specifically used for Flex and is necessary because
    // the renewalSummaryVM is disconnected from the main renewalVM (jobVM)
    // so not making sure the renewalSummaryVM is updated causes UI issues in the Flex modal
    const updateWizardAndSummaryData = async newRenewalVM => {
        setWizardLoading(true);
        // Updating renewalVM with response from Flex
        _.extend(renewalVM.value, newRenewalVM.value);
        updateWizardData(renewalVM);
        // Updating renewalSummaryVM with latest data
        await fetchRenewalSummary();
        setWizardLoading(false);
    };

    const isPremiumInfoVisible = !hasQuoteBlockingUWIssues() && isTransactionQuoted();
    const isUploadDocumentSectionVisible = !hasQuoteBlockingUWIssues() && isTransactionQuoted();

    const overrideProps = {
        wmicFlexAlertMessage: {
            jobVM: renewalSummaryVM,
        },
        quoteRenewalAlertMessage: {
            jobVM: renewalVM,
            underwritingIssues,
            // Hide alert message until underwriting issues are fetched
            visible: !!underwritingIssues,
        },
        quoteRenewalPagePremiumCosts: {
            premium: renewalSummaryVM?.currentPremiumData?.value,
            jobSummaryVM: renewalSummaryVM,
            enableFlex: true,
            updateJobVM: updateWizardAndSummaryData,
            enableBrokerFees: true,
            visible: isPremiumInfoVisible,
        },
        quoteRenewalPageUWIssues: {
            visible: !!underwritingIssues,
            underwritingIssues,
            quoteID: jobID,
            authHeader,
            jobVM: renewalVM.baseData,
            jobType: JobType.RENEWAL,
        },
        quoteRenewalPageSummary: {
            jobVM: renewalSummaryVM,
            visible: isPremiumInfoVisible,
        },
        quoteRenewalPageUploadDocuments: {
            documents: userDocuments,
            updateDocuments: setUserDocuments,
            value: {
                documentDTOs: userDocuments,
                canUploadDocument: true,
                jobNumber: jobID,
            },
            visible: isUploadDocumentSectionVisible,
        },
        quoteRenewalPageHOPrintableAgreement: {
            visible: renewalVM?.value?.lob === LINE_OF_BUSINESS.HOME_OWNER,
        },
    };

    const resolvers = {
        resolveComponentMap: {
            WMICQuoteSummary,
            WMICUnderwritingIssues,
            WMICDocuments,
            WMICHOPrintableAgreement,
        },
    };

    return (
        <WMICWizardChangeOrRenewalPage
            onNext={onNext}
            nextLabel={translator(messages.renew)}
            cancelLabel={translator(commonMessages.saveAndExit)}
            isSkipping={initialValidation}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={renewalVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                onValidationChange={onValidate}
            />
        </WMICWizardChangeOrRenewalPage>
    );
}

WMICPolicyRenewalQuote.propTypes = wizardProps;

export default withViewModelService(WMICPolicyRenewalQuote);
